import { CommonModule } from '@angular/common';
import { Component, computed, inject, OnInit, Signal, signal, OnDestroy } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { ModalService } from '@app/_services/modal.service';
import { FeatureSetup } from '@app/_interfaces/feature.interface';
import { FeatureStore } from '@app/_stores';
import { InputToggleComponent } from '@app/components/shared/inputs/input-toggle/input-toggle.component';
import { SvgComponent } from '@app/components/shared/svg/svg.component';
import { FeatureListComponent } from '../feature-list/feature-list.component';
import { Subject, takeUntil } from 'rxjs';
import { JournalSetupFormComponent } from '../../journals/journal-setup-form/journal-setup-form.component';
import { MoneyTrackerSetupFormComponent } from '../../money-tracker/money-tracker-setup-form/money-tracker-setup-form.component';
import { CalendarAccountFormComponent } from '../../calendar-integration/calendar-account-form/calendar-account-form.component';
import { HabitSetupFormComponent } from '../../habits/habit-setup-form/habit-setup-form.component';
import { FocusSetupFormComponent } from '../../focus-tracker/focus-setup-form/focus-setup-form.component';
import { CacheService } from '@app/_services/cache.service';
import { CalendarSyncStatus, EntityName } from '@app/_types/generic.type';
import { log } from '@app/_services/logger';
import { SettingsService } from '@app/_services/settings.service';
import { NotificationComponent } from '@app/components/common/notifications/notification/notification.component';
import { MeUserAvatarComponent } from '@app/components/common/account/me-user-avatar/me-user-avatar.component';
import { MembersModel } from '@app/_interfaces/generic.interface';
import { NoResultBlockComponent } from '@app/components/shared/no-result-block/no-result-block.component';
import { AnalyticsService } from '@app/_services/analytics.service';
import { StorageService } from '@app/_services/storage.servive';
import { AnalyticsScreen } from '@app/_types/analytics-events.types';
import { DependencyService } from '@app/_services/dependency.service';
import { ViewSettingsStore } from '@app/_stores';
import { toDate } from '@app/_services/date';
import { getCollaboratorCount } from '@app/_utils/members-model.utils';
import { BRAND_CONFIG } from '@app/_generated/brand.gen';

@Component({
  selector: 'app-feature',
  imports: [
    CommonModule,
    SvgComponent,
    InputToggleComponent,
    NotificationComponent,
    MeUserAvatarComponent,
    NoResultBlockComponent
  ],
  templateUrl: './feature.component.html',
  styleUrl: './feature.component.scss'
})

export class FeatureComponent implements OnInit, OnDestroy {

  readonly featureStore = inject(FeatureStore);
  readonly viewSettings = inject(ViewSettingsStore);
  unSubscribe = new Subject<void>();

  sortedFeatures: Signal<FeatureSetup[]> = computed(() => {
    const allFeatures = this.featureStore.features();
    const nonPremiumEntityTypes = ['todo', 'note', 'list'];

    // Separate non-premium and premium features
    const nonPremium = allFeatures.filter(f => nonPremiumEntityTypes.includes(f.entityType));
    const premium = allFeatures.filter(f => !nonPremiumEntityTypes.includes(f.entityType));

    // Sort premium features by createdAt (newest first)
    const sortedPremium = [...premium].sort((a, b) => {
      const dateA: number = a.createdAt != null && typeof a.createdAt === 'object' && '_seconds' in a.createdAt ? toDate(a.createdAt)?.getTime() ?? 0 : new Date(a.createdAt ?? 0).getTime();
      const dateB: number = b.createdAt != null && typeof b.createdAt === 'object' && '_seconds' in b.createdAt ? toDate(b.createdAt)?.getTime() ?? 0 : new Date(b.createdAt ?? 0).getTime();
      return dateB - dateA; // Descending order (newest first)
    });

    // Return non-premium first, then sorted premium
    return [...nonPremium, ...sortedPremium];
  });
  confirmConfig: { [key in EntityName]?: { title: string, message: string, buttonText: string, buttonColor: string } } = {
    todo: {
      title: this.ss.t('hideFeature_todoTitle'),
      message: this.ss.t('hideFeature_todoContent', { brandName: BRAND_CONFIG.name }),
      buttonText: this.ss.t('hideFeature_disable'),
      buttonColor: 'color-11'
    },
    note: {
      title: this.ss.t('hideFeature_noteTitle'),
      message: this.ss.t('hideFeature_noteContent', { brandName: BRAND_CONFIG.name }),
      buttonText: this.ss.t('hideFeature_disable'),
      buttonColor: 'color-11'
    },
    list: {
      title: this.ss.t('hideFeature_listTitle'),
      message: this.ss.t('hideFeature_listContent', { brandName: BRAND_CONFIG.name }),
      buttonText: this.ss.t('hideFeature_disable'),
      buttonColor: 'color-11'
    },
    focus: {
      title: this.ss.t('hideFeature_focusTrackerTitle'),
      message: this.ss.t('hideFeature_focusTrackerContent', { brandName: BRAND_CONFIG.name }),
      buttonText: this.ss.t('hideFeature_disable'),
      buttonColor: 'color-11'
    },
    habit: {
      title: this.ss.t('hideFeature_habitTitle'),
      message: this.ss.t('hideFeature_habitContent', { brandName: BRAND_CONFIG.name }),
      buttonText: this.ss.t('hideFeature_disable'),
      buttonColor: 'color-11'
    },
    journal: {
      title: this.ss.t('hideFeature_journalTitle'),
      message: this.ss.t('hideFeature_journalContent', { brandName: BRAND_CONFIG.name }),
      buttonText: this.ss.t('hideFeature_disable'),
      buttonColor: 'color-11'
    },
    moneyTracker: {
      title: this.ss.t('hideFeature_moneyTrackerTitle'),
      message: this.ss.t('hideFeature_moneyTrackerContent', { brandName: BRAND_CONFIG.name }),
      buttonText: this.ss.t('hideFeature_disable'),
      buttonColor: 'color-11'
    },
    calendarIntegration: {
      title: this.ss.t('hideFeature_calendarTitle'),
      message: this.ss.t('hideFeature_calendarContent', { brandName: BRAND_CONFIG.name }),
      buttonText: this.ss.t('hideFeature_disable'),
      buttonColor: 'color-11'
    }
  }

  screenEnterTime: Signal<Date> = signal(new Date());

  constructor(
    public dialog: MatDialog,
    private modalService: ModalService,
    public cc: CacheService,
    public ss: SettingsService,
    private as: AnalyticsService,
    private storageService: StorageService,
    public ds: DependencyService,
    private router: Router,
  ) {

  }

  ngOnInit() {
    this.analyticsInit();
  }

  navigateToToday() {
    this.router.navigate(['/today']);
  }

  addFeatures() {
    this.modalService.openLevel1(FeatureListComponent);
  }

  async toggleChange(feature: FeatureSetup, value: boolean) {
    // value=true means enabled (visible), value=false means hidden
    // hide parameter for toggle methods: true=hide, false=show
    const hide = !value;

    switch (feature.entityType) {
      case 'focus':
        this.viewSettings.toggleFocusVisibility(feature.id, hide);
        await this.viewSettings.persist();
        break;
      case 'todo':
        this.viewSettings.updateSetting('showTodoFeature', value);
        await this.viewSettings.persist();
        break;
      case 'note':
        this.viewSettings.updateSetting('showNoteFeature', value);
        await this.viewSettings.persist();
        break;
      case 'list':
        this.viewSettings.updateSetting('showListFeature', value);
        await this.viewSettings.persist();
        break;
      case 'boolean':
      case 'timer':
      case 'numeric':
      case 'single':
      case 'multiple':
        this.viewSettings.toggleHabitVisibility(feature.id, hide);
        await this.viewSettings.persist();
        break;
      case 'journal':
        this.viewSettings.toggleJournalVisibility(feature.id, hide);
        await this.viewSettings.persist();
        break;
      case 'moneyTracker':
        this.viewSettings.toggleMoneyTrackerVisibility(feature.id, hide);
        await this.viewSettings.persist();
        break;
      case 'google':
      case 'microsoft':
        this.viewSettings.toggleCalendarVisibility(feature.id, hide);
        await this.viewSettings.persist();
        break;
      default:
        log.warn('FeatureComponent.toggleChange', `Unknown entityType: ${feature.entityType}`, { entityType: feature.entityType });
        break;
    }
  }

  // Calendar status helpers
  getCalendarStatusText(syncStatus?: CalendarSyncStatus): string {
    switch (syncStatus) {
      case 'syncing': return this.ss.t('common_syncingEvents') ?? 'Syncing';
      case 'failed': return this.ss.t('calendarSync_failed');
      case 'error': return this.ss.t('calendar_reAuthRequired');
      case 'synced':
      default: return this.ss.t('common_active');
    }
  }

  getCalendarStatusIcon(syncStatus?: CalendarSyncStatus): string {
    switch (syncStatus) {
      case 'syncing': return 'resync';
      case 'failed': return 'warningAlert';
      case 'error': return 'importantAlert';
      case 'synced':
      default: return 'circleCheck';
    }
  }

  getDuration(startDate: string | null, endDate: string | null, repeat?: string | null): string {
    if (!startDate) return '';

    const startDateLabel = startDate === this.cc.today() ? this.ss.t('common_today') : this.ss.getFormattedDate(startDate, 'd MMM yyyy');

    // Check if repeat uses occurrences (COUNT)
    if (repeat) {
      const countMatch = repeat.match(/COUNT=(\d+)/);
      if (countMatch) {
        const count = Number(countMatch[1]);
        const fromText = this.ss.t('dateRangePicker_previewDateToNeverEndsSelect', { startDate: startDateLabel });
        const occurrenceText = count > 1
          ? this.ss.t('common_occurrenceCount', { occurenceValue: count.toString() })
          : `${count} ${this.ss.t('repeatEndOptions_occurrence')}`;
        return `${fromText}, ${occurrenceText}`;
      }
    }

    if (endDate) {
      const endDateLabel = this.ss.getFormattedDate(endDate, 'd MMM yyyy');
      return `${startDateLabel} - ${endDateLabel}`;
    } else if (repeat) {
      return `${startDateLabel} - ${this.ss.t('datePicker_neverEnds')}`;
    } else {
      return startDateLabel;
    }
  }

  openFeatureSetup(feature: FeatureSetup) {
    switch (feature.entityType) {
      case 'boolean':
      case 'timer':
      case 'numeric':
      case 'single':
      case 'multiple':

        const data = {
          mode: 'edit',
          value: feature.entity,
          habitType: feature.entityType,
        };
        if (this.cc.isNarrowScreen()) {
          const setupDialog = this.modalService.openMainContent(
            HabitSetupFormComponent,
            data,
            { disableClose: true, autoFocus: false }
          );
        } else {
          const setupDialog = this.modalService.openLevel1(
            HabitSetupFormComponent,
            data,
            { disableClose: true, autoFocus: false }
          );
        }
        break;
      case 'journal':

        const journalData = {
          mode: 'edit',
          value: feature.entity,
        }

        if (this.cc.isNarrowScreen()) {
          const setupDialog = this.modalService.openMainContent(
            JournalSetupFormComponent,
            journalData,
            { disableClose: true, autoFocus: false }
          );
        } else {
          const setupDialog = this.modalService.openLevel1(
            JournalSetupFormComponent,
            journalData,
            { disableClose: true, autoFocus: false }
          );
        }
        break;
      case 'moneyTracker':
        const moneyData = {
          mode: 'edit',
          value: feature.entity,
        }

        if (this.cc.isNarrowScreen()) {
          const setupDialog = this.modalService.openMainContent(
            MoneyTrackerSetupFormComponent,
            moneyData,
            { disableClose: true, autoFocus: false }
          );
        } else {
          const setupDialog = this.modalService.openLevel1(
            MoneyTrackerSetupFormComponent,
            moneyData,
            { disableClose: true, autoFocus: false }
          );
        }
        break;

      case 'focus':

        const focusData = {
          mode: 'edit',
          value: feature.entity,
        }

        if (this.cc.isNarrowScreen()) {
          this.modalService.openMainContent(
            FocusSetupFormComponent,
            focusData,
            { disableClose: true, autoFocus: false }
          );
        } else {
          this.modalService.openLevel1(
            FocusSetupFormComponent,
            focusData,
            { disableClose: true, autoFocus: false }
          );
        }
        break;

      case 'google':
      case 'microsoft':
        if (this.cc.isNarrowScreen()) {
          const setupCalendarDialog = this.modalService.openMainContent(CalendarAccountFormComponent,
            {
              mode: 'edit',
              value: feature.entity,
            },
            { disableClose: true, autoFocus: false }
          );
        } else {
          const setupCalendarDialog = this.modalService.openModalLevel1(CalendarAccountFormComponent,
            {
              mode: 'edit',
              value: feature.entity,
            },
            { disableClose: true, autoFocus: false }
          );
        }
        break;

      default:
        log.warn('FeatureComponent.openFeatureSetup', `Unknown entityType: ${feature.entityType}`, { entityType: feature.entityType });
        break;
    }

  };

  /** Collaborator count from collaborators map (NOT memberUids which only has registered users) */
  collabCount(members: MembersModel | undefined) {
    return getCollaboratorCount(members);
  };

  analyticsInit() {
    this.as.analytics$.pipe(takeUntil(this.unSubscribe)).subscribe((init: boolean) => {
      if (init) {
        const previousScreen = this.storageService.getCurrentScreen() as AnalyticsScreen;
        this.as.screenViewEvent('drawerFeatures', previousScreen);
      }
    });
  }

  ngOnDestroy() {
    this.as.screenExitEvent('drawerFeatures', this.screenEnterTime());
    this.unSubscribe.complete();
    this.unSubscribe.next();
  }

}
